<template>
  <div class="fts-form-element fts-form-element-theme" :class="{ fw: fullWidthElement }">
    <!-- custom:description -->
    <template v-if="item.display || item.content">
      <div
        class="fts-form-group form-element-custom-description" 
        v-html="item.content"
      />
    </template>
    <!-- custom:image -->
    <template v-else-if="item.input == 'image'">
      <div class="fts-form-group" v-if="item.imageSource">
        <img
          :src="getImagePath(item.imageSource.path)"
          class="form-element-custom-image"
        />
      </div>
    </template>
    <!-- custom:button -->
    <div class="fts-form-group" v-else-if="item.input == 'button'">
      <fts-btn
        outline
        :color="'primary'"
        text-transform-init
        :disabled="item.readOnlyFormula || item.readOnly"
        @click="clientAction(item)"
      >
        {{ item.label ? item.label : "(unnamed)" }}
      </fts-btn>
    </div>
    <!-- empty space -->
    <div v-else-if="item.input == 'empty'" />
    <!-- fts form element wrapper -->
    <div
      class="fts-form-element-wrapper"
      :class="comfortDesign ? 'comfort-form-design' : 'compact-form-design'"
      ref="element"
      v-else
    >
      <!-- form label -->
      <label
        class="fts-form-label with-icon"
        :class="{
          'with-error': error,
          'label-top': fullWidthElement,
        }"
        v-if="item.input !== 'empty' && item.label !== false"
      >
        {{labelDisplayName}}
        <i
          class="form-label-icon fts-icon-calendar-check"
          :class="{ required: required && !formElementDisplay }"
          v-if="item.due"
        />
        <i
          class="form-label-icon fts-icon-user-check"
          :class="{ required: required && !formElementDisplay }"
          v-else-if="item.assignee"
        />
        <i
          class="form-label-icon"
          :class="[
            getIcon,
            { required: required && !formElementDisplay },
          ]"
          v-else
        />
      </label>
      <!-- form helper -->
      <div class="fts-form-helper" v-if="comfortDesign && item.helper">
        {{item.helper}}
      </div>
      <!-- form group -->
      <div class="fts-form-group">
        <!-- slot -->
        <slot 
          name="formElement"
          :formData="formData"
          :item="item"
          :onInput="onInput"
          :onBlur="onBlur"
          v-if="isFormElementSlot"
        />
        <!-- display -->
        <fts-display
          :vectorId="vectorId"
          :item="item"
          :formData="formData"
          :view="view"
          v-else-if="formElementDisplay"
        />
        <!-- custom formatted text -->
        <cleave
          class="fts-form-control"
          v-model="formData[item.fieldName]"
          :options="item.options"
          @input="onInput()"
          v-else-if="item.input == 'formattedText'"
        />
        <!-- yes no -->
        <div
          v-else-if="
            item.type == 1 ||
            formElementInput == 'boolean' ||
            formElementInput == 'toggle'
          "
        >
          <checkbox
            v-model="formData[item.fieldName]"
            :color="'#279ec6'"
            :size="20"
            @change="onInput()"
          />
        </div>
        <!-- integer -->
        <fts-integer
          :item="item"
          :formData="formData"
          controls
          inline
          @input="onInput()"
          @blur="onBlur()"
          v-else-if="
            item.type == 2 ||
            formElementInput == 'integer'
          "
        />
        <!-- number / money -->
        <fts-number-currency
          :item="item"
          :formData="formData"
          @input="onInput()"
          @blur="onBlur()"
          v-else-if="
            item.type == 3 ||
            item.type == 4 ||
            formElementInput == 'number' ||
            formElementInput == 'currency'
          "
        />
        <!-- date time -->
        <fts-date-time
          :item="item"
          :formData="formData"
          @input="onInput()"
          @close="validatorTouch()"
          v-else-if="
            item.type == 5 ||
            item.type == 6 ||
            formElementInput == 'dateTime' ||
            formElementInput == 'date' ||
            formElementInput == 'time'
          "
        />
        <!-- location -->
        <fts-location
          :item="item"
          :formData="formData"
          @input="onInput()"
          @close="validatorTouch()"
          v-else-if="
            item.type == 9 ||
            formElementInput == 'location'
          "
        />
        <!-- file upload -->
        <fts-file-upload
          :formData="formData"
          :item="item"
          :vectorId="vectorId"
          @input="onInput()"
          v-else-if="
            item.type == 10 ||
            item.type == 11 ||
            item.type == 12 ||
            item.type == 13 ||
            formElementInput == 'imageUpload' ||
            formElementInput == 'imagesUpload' ||
            formElementInput == 'fileUpload' ||
            formElementInput == 'filesUpload'
          "
        />
        <!-- table -->
        <fts-table-edit
          :formData="formData"
          :item="item"
          :vectorId="vectorId"
          :validator="validator && validator[item.fieldName]"
          v-else-if="item.type == 14 || formElementInput == 'table'"
        />
        <!-- multiline text -->
        <fts-text-area
          :item="item"
          :formData="formData"
          :rows="item.rows"
          @input="onInput()"
          @blur="onBlur()"
          v-else-if="
            item.type == 16 ||
            formElementInput == 'textArea'
          "
        />
        <!-- username -->
        <fts-user
          :formData="formData"
          :item="item"
          @input="onInput()"
          @blur="onBlur()"
          v-else-if="
            item.type == 19 ||
            formElementInput == 'username'
          "
        />
        <!-- profile (module) -->
        <fts-user
          :item="item"
          :formData="formData"
          @input="onInput()"
          @blur="onBlur()"
          v-else-if="
            item.type == 20 ||
            formElementInput == 'dropDownUser'
          "
        />
        <!-- content -->
        <fts-content   
          :item="item"
          :formData="formData"
          :data="formData[item.fieldName]"
          @input="onInput()"
          v-else-if="
            item.type == 22 ||
            formElementInput == 'content'
          "
        />
        <!-- context -->
        <fts-dropdown
          :multiple="true"
          :item="item"
          :formData="formData"
          :taggable="true"
          @input="onInput()"
          @blur="onBlur()"
          v-else-if="
            item.type == 24 ||
            formElementInput == 'contextSelection'
          "
        />
        <!-- phone number -->
        <fts-phone-number
          :item="item"
          :formData="formData"
          @input="onInput()"
          @blur="onBlur()"
          v-else-if="
            item.type == 26 ||
            formElementInput == 'phone'
          "
        />
        <!-- select (module) -->
        <fts-dropdown
          :item="item"
          :formData="formData"
          :taggable="true"
          @input="onInput()"
          @blur="onBlur()"
          v-else-if="
            item.type == 7 ||
            item.type == 27 ||
            formElementInput == 'dropDown' ||
            formElementInput == 'select'
          "
        />
        <!-- tag (module) -->
        <fts-dropdown
          :item="item"
          :formData="formData"
          :taggable="true"
          @input="onInput()"
          @blur="onBlur()"
          v-else-if="item.type == 28"
        />
        <!-- tag (module, multiple) -->
        <fts-dropdown
          :item="item"
          :formData="formData"
          :multiple="true"
          @input="onInput()"
          @blur="onBlur()"
          v-else-if="
            item.type == 28 ||
            formElementInput == 'multipleDropDown'
          "
        />
        <!-- signature -->
        <fts-signature
          :item="item"
          :formData="formData"
          :vectorId="vectorId"
          @input="onInput()"
          v-else-if="
            item.type == 29 ||
            formElementInput == 'signature'
          "
        />
        <!-- list -->
        <fts-list
          :item="item"
          :formData="formData"
          :view="view"
          :validator="validator && validator[item.fieldName]"
          v-else-if="
            item.type == 15 ||
            formElementInput == 'list'
          "
        />
        <!-- date range -->
        <fts-period
          :item="item"
          :formData="formData"
          v-else-if="
            item.type == 30 ||
            formElementInput == 'dateRange'
          "
        />
        <!-- toggle switch -->
        <fts-toggle-switch
          :formData="formData"
          :item="{ fieldName: item.fieldName }"
          mat-class
          :label="false"
          :size="18"
          v-else-if="formElementInput === 'toggleSwitch'"
        />  
        <!-- password -->
        <input
          class="fts-form-control"
          type="password"
          autocomplete="new-password"
          v-model="formData[item.fieldName]"
          @input="onInput()"
          @blur="onBlur()"
          v-else-if="formElementInput === 'password'"
        />
        <!-- default -->
        <input
          class="fts-form-control"
          type="text"
          autocomplete="form-element-input"
          v-model="formData[item.fieldName]"
          @input="onInput()"
          @blur="onBlur()"
          @keyup.enter="onKeyupEnter"
          v-else
        />
        <!-- error message -->
        <div class="fts-form-error" v-if="formError && error">
          <slot name="errorMessage" :error="error">{{ error }}</slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import Cleave from "vue-cleave-component";
Vue.use(Cleave);

import commons from "../commons/commons";

import FTSDropdown from "./FTSDropdown";
import FTSTextArea from "./FTSTextArea";
import FTSDateTime from "./FTSDateTime";
import FTSInteger from "./FTSInteger";
import FTSLocation from "./FTSLocation";
import FTSFileUpload from "./FTSFileUpload";
import FTSTableEdit from "./FTSTableEdit";
import FTSContent from "./FTSContent";
import FTSSignature from "./FTSSignature";
import FTSDisplay from "./FTSDisplay";
import FTSUser from "./FTSUser";
import FTSList from "./FTSList";
import FTSPeriod from "./FTSPeriod";
import FTSNumberCurrency from "./FTSNumberCurrency";
import FTSPhoneNumber from "./FTSPhoneNumber";
import FTSToggleSwitch from "./FTSToggleSwitch";
import FTSBtn from "./FTSBtn";

import { clientStore } from "../store/clientStore";

import Checkbox from "vue-material-checkbox";

export default {
  components: {
    "fts-dropdown": FTSDropdown,
    "fts-text-area": FTSTextArea,
    "fts-date-time": FTSDateTime,
    "fts-integer": FTSInteger,
    "fts-location": FTSLocation,
    "fts-file-upload": FTSFileUpload,
    "fts-table-edit": FTSTableEdit,
    "fts-content": FTSContent,
    "fts-signature": FTSSignature,
    "fts-display": FTSDisplay,
    "fts-user": FTSUser,
    "fts-list": FTSList,
    "fts-list": FTSList,
    "fts-period": FTSPeriod,
    "fts-btn": FTSBtn,
    "fts-number-currency": FTSNumberCurrency,
    "fts-phone-number": FTSPhoneNumber,
    "fts-toggle-switch": FTSToggleSwitch,
    checkbox: Checkbox,
  },
  props: {
    vectorId: [String, Number],
    item: Object,
    formData: Object,
    validator: Object,
    username: String,
    context: Object,
    view: Boolean,
    comfortDesign: Boolean,
    formError: {
      type: Boolean,
      default: true,
    },
  },
  methods: {
    onInput() {
      let fieldValidator = this.validator && this.validator[this.item.fieldName];

      if (this.item.onChanged != null) 
        this.item.onChanged();

      if (
        fieldValidator &&
        (
          fieldValidator.required === false || 
          (this.item.type === 22 && fieldValidator.htmlValidator === false)
        )
      )
        fieldValidator.$touch();
    },
    onBlur() {
      this.validatorTouch();
    },
    onKeyupEnter() {
      this.$emit("keyup:enter");
    },
    validatorTouch() {
      let field = this.validator && this.validator[this.item.fieldName];
      field && field.$touch();
    },
    getImagePath(path) {
      if (path.startsWith('/s/')) {
        const url = path.split('/');
        const filename = encodeURIComponent(url.pop());
        const encodeURL = `${url.join("/")}/${filename}`;
        return `/api/d/${this.vectorId}-${this.item.id}${encodeURL}`;
      } else {
        const url = path.split('/');
        const filename = encodeURIComponent(url.pop());
        const encodeURL = `${url.join("/")}/${filename}`;
        return `${encodeURL}`;
      }
    },
    clientAction(item) {
      if (item.eventName && item.eventName != "") {
        this.$emit('eventTriggered', item.eventName)
      } else {
        let formData = this.formData;
        eval(item.frontendScript);
      }
    },
  },
  computed: {
    getIcon() {
      const item = this.item;
      if (item.icon) 
        return item.icon;
      else if ((item.type === undefined || item.type === null) && item.input)
        try {
          if (item.input === 'password')
            return commons.informationIcon(0);
          else if (item.input === 'toggleSwitch')
            return commons.informationIcon(1);
          else
            return commons.informationIcon(
              commons.informationTypes.find((x) => x.input == item.input).id
            );
        } catch (err) {}
      else 
        return commons.informationIcon(item.type);
    },
    labelDisplayName() {
      if (this.item.label)
        return this.item.label;
      else if (this.item.displayName) 
        return this.item.displayName;
      else if (this.item.fieldName)
        return commons.getDisplayName(this.item.fieldName);
    },
    contextField() {
      if (
        clientStore &&
        clientStore.state.workspace &&
        clientStore.state.workspace.contextModuleId &&
        this.item.connectionId
      ) {
        console.log(clientStore.state.workspace);

        return (
          clientStore.state.workspace.contextModuleId.toString() ==
          this.item.connectionId.toString()
        );
      }
      return false;
    },
    required() {
      return this.item.required && !this.item.readOnly;
    },
    error() {
      try {
        let validator = this.validator[this.item.fieldName];
        const displayName = this.labelDisplayName;
        if (validator.$error) {
          /* email */
          if (validator.email === false)
            return "Please enter email address in format: yourname@example.com";
          /* phone */
          if (validator.phone == false)
            return "Please enter valid phone number.";
          /* link */
          if (validator.url === false)
            return "Please enter valid URL address.";
          /* password */
          else if (validator.required && validator.password === false)
            return `${
              displayName ? displayName : "Password"
            } must contain 1 alphabet, 1 numeric, and at least 8 characters.`;
          /* same as */
          else if (validator.required && validator.sameAs === false)
            return `${displayName ? displayName : "Field"} must be identical.`;
          /* table */
          else if (this.item.type == 14) {
            if (validator.required === false)
              return `${
                displayName ? displayName : "Table"
              } at least must have one row.`;

            // let err = "";
            const fields = Object.values(validator.$each.$iter).filter(
              (x) => x.$invalid
            );
            return `Please ensure columns value are in a proper format.`
            // const email = fields.some((x) =>
            //   Object.values(x)
            //     .filter((y) => y.$invalid)
            //     .some((x) => x.email === false)
            // );
            // const phone = fields.some((x) =>
            //   Object.values(x)
            //     .filter((y) => y.$invalid)
            //     .some((x) => x.phone === false)
            // );
            // const required = fields.some((x) =>
            //   Object.values(x)
            //     .filter((y) => y.$invalid) 
            //     .some((x) => x.email !== false)
            // );
            // if (required) {
            //   err += "Field(s) cannot be empty.";
            // }
            // if (email) {
            //   err += " Please enter email address in format: yourname@example.com.";
            // }
            // if (phone) {
            //   err += " Please enter valid phone number.";
            // }
            // return err.trim();
          }
          // default
          else return `${displayName ? displayName : "Field"} is required.`;
        }
      } catch (err) {}
    },
    submitted() {
      return this.validation && this.validation.submitted;
    },
    fullWidthElement() {
      return (
        this.item.type == 14 ||
        this.item.type == 15 ||
        this.formElementInput === "table" ||
        this.formElementInput === "list"
      );
    },
    formElementDisplay() {
      return (
        this.item.display ||
        this.item.input == "display" ||
        this.item.readOnly ||
        this.view ||
        (this.contextField && this.item.type != 24) ||
        (this.username && this.item.fieldName == 'username')
      );
    },
    formElementInput() {
      if (!commons.isInteger(this.item.type) || this.item.custom) {
        return this.item.input;
      }
    },
    isFormElementSlot() {
      return !!this.$slots.formElement || !!this.$scopedSlots.formElement
    },
  },
  mounted() {
    if (
      this.contextField &&
      this.item.type != 24 &&
      clientStore &&
      clientStore.state.workspaceContext
    ) {
      this.formData[this.item.fieldName] = clientStore.state.workspaceContext.name;
      this.formData[this.item.fieldName + "Id"] = clientStore.state.workspaceContext.id;
    }
    if (
      !this.item.readOnly &&
      !this.item.display &&
      this.item.input != "display"
    ) {
      if (this.formData[this.item.fieldName] === undefined) {
        Vue.set(this.formData, this.item.fieldName, null);
      }
    }

    if (this.formElementInput == "dropDown") {
      if (this.formData[this.item.fieldName + "Id"] === undefined) {
        Vue.set(this.formData, this.item.fieldName + "Id", 0);
      }

      if (this.formData[this.item.fieldName + "Model"] === undefined) {
        Vue.set(this.formData, this.item.fieldName + "Model", null);
      }
    } else if (
      this.formElementInput == "imagesUpload" ||
      this.formElementInput == "filesUpload"
    ) {
      if (this.formData[this.item.fieldName] === undefined) {
        Vue.set(this.formData, this.item.fieldName, []);
      }
    } else if (this.formElementInput == "user") {
      if (this.formData[this.item.fieldName] === undefined) {
        Vue.set(this.formData, this.item.fieldName + "Model", null);
        Vue.set(this.formData, this.item.fieldName + "Username", null);
        Vue.set(this.formData, this.item.fieldName + "Id", null);
      }
    } else if (this.formElementInput == "table") {
      if (
        this.formData[this.item.fieldName] === undefined ||
        this.formData[this.item.fieldName] === null
      ) {
        Vue.set(this.formData, this.item.fieldName, []);
      }
    }
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-form-element {
  max-width: 100%;
  &.fw {
    flex: 1 1 100%;
  }
  &:not(.fw) {
    min-width: 0;
    flex: 1 0 400px;
    .fts-form-element-wrapper {
      display: flex;
      flex-wrap: wrap;
      > .fts-form-group {
        min-width: 0;
        flex-grow: 1;
      }
      &.compact-form-design {
        flex-wrap: nowrap;
        > .fts-form-label {
          margin: calc((#{$height} / 2) - 0.475rem) 10px 0 0;
          width: 180px;
          min-width: 180px;
          text-align: right;
        }
      }
    }
  }
  .form-element-custom-description {
    line-height: 1.5;
  }
  .form-element-custom-image {
    max-height: 200px;
    max-width: 100%;
  }
}

//temp
.m-chckbox--container {
  margin: 9px 0;
}
</style>


